import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { MessageCircle, Copy, ExternalLink, CheckCircle, Info, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_LABELS = {
  beginner: { tr: 'Başlangıç', en: 'Beginner' },
  intermediate: { tr: 'Orta', en: 'Intermediate' },
  advanced: { tr: 'İleri', en: 'Advanced' },
  custom: { tr: 'Özel', en: 'Custom' },
};

export default function MyCabinCrewGroup() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchGroup = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/my-cabin-crew-group`, { headers: authHeaders() });
      setData(res.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroup(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!loading && data && !data.has_group) {
      if (data.reason === 'no_enrollment' || data.reason === 'no_level') {
        navigate('/cabin-crew', { replace: true });
      }
    }
  }, [loading, data, navigate]);

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(lang === 'tr' ? 'Link kopyalandı' : 'Link copied');
    } catch {
      toast.error(lang === 'tr' ? 'Kopyalama başarısız' : 'Copy failed');
    }
  };

  const markJoined = async (platform) => {
    try {
      await axios.post(
        `${API}/my-cabin-crew-group/mark-joined`,
        { platform },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Isaretlendi' : 'Marked as joined');
      fetchGroup();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
        <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !data.has_group) {
    return (
      <div className="min-h-screen bg-[var(--bg-app)] p-4" data-testid="my-group-empty">
        <div className="max-w-xl mx-auto mt-20 bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <Info size={24} className="text-blue-600" />
            <h1 className="font-display text-xl font-bold text-blue-900">
              {lang === 'tr' ? 'Henüz Gruba Atanmadınız' : 'Not Assigned to a Group Yet'}
            </h1>
          </div>
          <p className="text-sm text-blue-900 mb-4">
            {lang === 'tr'
              ? 'Seviyeniz belirlendi, admin size uygun bir WhatsApp grup ataması yakında yapılacak. Atandığınızda buradan görüntüleyebilirsiniz.'
              : 'Your level is assigned. An admin will assign you to a suitable WhatsApp group soon.'}
          </p>
          <button
            onClick={() => navigate('/cabin-crew')}
            className="px-4 py-2 rounded-xl border-2 border-blue-300 text-blue-900 font-bold hover:bg-blue-100 flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            {lang === 'tr' ? 'Kabin Memurluğuna Dön' : 'Back to Cabin Crew'}
          </button>
        </div>
      </div>
    );
  }

  const group = data.group;
  const levelLabel = LEVEL_LABELS[group.level]
    ? (lang === 'tr' ? LEVEL_LABELS[group.level].tr : LEVEL_LABELS[group.level].en)
    : group.level;

  return (
    <div className="min-h-screen bg-[var(--bg-app)]" data-testid="my-group-page">
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white py-10">
        <div className="max-w-3xl mx-auto px-4">
          <button
            onClick={() => navigate('/cabin-crew')}
            className="text-white/80 hover:text-white text-xs font-bold flex items-center gap-1 mb-3"
          >
            <ArrowLeft size={14} /> {lang === 'tr' ? 'Kabin Memurluğuna Dön' : 'Back to Cabin Crew'}
          </button>
          <div className="flex items-center gap-3 mb-1">
            <MessageCircle size={28} />
            <h1 className="font-display text-3xl font-bold">
              {lang === 'tr' ? 'Grubum' : 'My Group'}
            </h1>
          </div>
          <p className="text-green-100 text-sm">
            {lang === 'tr'
              ? 'Eğitim duyuruları ve sınıf arkadaşlarınızla iletişim için grup linkiniz.'
              : 'Your group link for training announcements and classmates.'}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl p-5 border-2 border-gray-100 mb-4" data-testid="group-info">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <MessageCircle size={24} className="text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-lg mb-1">
                {lang === 'tr' ? group.name : (group.name_en || group.name)}
              </h2>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="px-2 py-0.5 bg-sky-100 text-sky-800 rounded-full font-bold">
                  {levelLabel}
                </span>
                <span className="text-[var(--text-muted)]">
                  {group.current_count}/{group.capacity} {lang === 'tr' ? 'uye' : 'members'}
                </span>
              </div>
              {((lang === 'tr' ? group.description_tr : group.description_en) || '').trim() && (
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  {lang === 'tr' ? group.description_tr : group.description_en}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5 mb-4" data-testid="whatsapp-card">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={20} className="text-green-700" />
            <h3 className="font-bold">
              {lang === 'tr' ? 'WhatsApp Grubu' : 'WhatsApp Group'}
            </h3>
            {data.joined_whatsapp && (
              <span className="ml-auto px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                <CheckCircle size={10} /> {lang === 'tr' ? 'Katıldınız' : 'Joined'}
              </span>
            )}
          </div>

          <div className="bg-white border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2 mb-3">
            <span className="text-xs font-mono text-gray-600 flex-1 truncate">{group.whatsapp_invite_url}</span>
            <button
              onClick={() => copyLink(group.whatsapp_invite_url)}
              className="text-green-700 hover:text-green-900"
              title={lang === 'tr' ? 'Kopyala' : 'Copy'}
              data-testid="copy-whatsapp"
            >
              <Copy size={14} />
            </button>
          </div>

          <div className="flex gap-2 flex-wrap">
            <a
              href={group.whatsapp_invite_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-600 text-white rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-green-700"
              data-testid="open-whatsapp"
            >
              <ExternalLink size={14} />
              {lang === 'tr' ? "WhatsApp'ta Ac" : 'Open in WhatsApp'}
            </a>
            {!data.joined_whatsapp && (
              <button
                onClick={() => markJoined('whatsapp')}
                className="px-4 py-2 border-2 border-green-600 text-green-700 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-green-100"
                data-testid="mark-joined-whatsapp"
              >
                <CheckCircle size={14} />
                {lang === 'tr' ? 'Gruba Katıldım' : 'I Joined the Group'}
              </button>
            )}
          </div>
        </div>

        {group.telegram_invite_url && (
          <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-5">
            <h3 className="font-bold mb-2">Telegram</h3>
            <a
              href={group.telegram_invite_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-700 hover:text-sky-900 text-sm flex items-center gap-1"
            >
              <ExternalLink size={14} /> {group.telegram_invite_url}
            </a>
          </div>
        )}

        <div className="text-[11px] text-[var(--text-muted)] mt-4 text-center">
          {lang === 'tr'
            ? 'WhatsApp grubuna manuel olarak katılmanız gerekir. Gruba katıldıktan sonra admin takip için işaretlemenizi isteyebilir.'
            : 'You need to manually join the WhatsApp group. After joining, admin may ask you to mark it for tracking.'}
        </div>
      </div>
    </div>
  );
}
